import { useState, useEffect } from 'react';
import type { KBTagTreeNode, KBTagSummary } from '../types/knowledgeBase';

interface TagTreeNodeProps {
  node: KBTagTreeNode;
  level: number;
  selectedTagId?: number | null;
  onSelectTag?: (tagId: number | null) => void;
  expandedNodes: Set<number>;
  toggleExpanded: (nodeId: number) => void;
}

function TagTreeNode({
  node,
  level,
  selectedTagId,
  onSelectTag,
  expandedNodes,
  toggleExpanded,
}: TagTreeNodeProps) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedTagId === node.id;

  return (
    <div>
      <div
        className={`flex items-center py-1.5 px-2 rounded cursor-pointer transition-colors ${
          isSelected
            ? 'bg-gray-900 text-white'
            : 'hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectTag?.(isSelected ? null : node.id)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(node.id);
            }}
            className={`mr-1 w-4 h-4 flex items-center justify-center text-xs ${
              isSelected ? 'text-white' : 'text-gray-500'
            }`}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="mr-1 w-4" />
        )}
        {node.color && (
          <span
            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: node.color }}
          />
        )}
        <span className="flex-1 text-sm truncate">{node.name}</span>
        {node.kb_count > 0 && (
          <span
            className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
              isSelected
                ? 'bg-white/20 text-white'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {node.kb_count}
          </span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TagTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedTagId={selectedTagId}
              onSelectTag={onSelectTag}
              expandedNodes={expandedNodes}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface TagTreeProps {
  tags: KBTagTreeNode[];
  selectedTagId?: number | null;
  onSelectTag?: (tagId: number | null) => void;
  showAllOption?: boolean;
  allLabel?: string;
  className?: string;
}

export default function TagTree({
  tags,
  selectedTagId,
  onSelectTag,
  showAllOption = true,
  allLabel = 'All Knowledge Bases',
  className = '',
}: TagTreeProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  // Auto-expand all nodes on initial load
  useEffect(() => {
    const getAllNodeIds = (nodes: KBTagTreeNode[]): number[] => {
      return nodes.flatMap((node) => [
        node.id,
        ...getAllNodeIds(node.children || []),
      ]);
    };
    setExpandedNodes(new Set(getAllNodeIds(tags)));
  }, [tags]);

  const toggleExpanded = (nodeId: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  if (tags.length === 0) {
    return (
      <div className={`text-sm text-gray-500 py-2 ${className}`}>
        No tags created yet
      </div>
    );
  }

  return (
    <div className={className}>
      {showAllOption && (
        <div
          className={`flex items-center py-1.5 px-2 rounded cursor-pointer transition-colors mb-1 ${
            selectedTagId === null
              ? 'bg-gray-900 text-white'
              : 'hover:bg-gray-100'
          }`}
          onClick={() => onSelectTag?.(null)}
        >
          <span className="mr-1 w-4" />
          <span className="flex-1 text-sm">{allLabel}</span>
        </div>
      )}
      {tags.map((node) => (
        <TagTreeNode
          key={node.id}
          node={node}
          level={0}
          selectedTagId={selectedTagId}
          onSelectTag={onSelectTag}
          expandedNodes={expandedNodes}
          toggleExpanded={toggleExpanded}
        />
      ))}
    </div>
  );
}

// Component for displaying tag badges (used in KB cards)
interface TagBadgesProps {
  tags: KBTagSummary[];
  maxDisplay?: number;
  size?: 'sm' | 'md';
}

export function TagBadges({ tags, maxDisplay = 3, size = 'sm' }: TagBadgesProps) {
  if (!tags || tags.length === 0) return null;

  const displayTags = tags.slice(0, maxDisplay);
  const remainingCount = tags.length - maxDisplay;

  const sizeClasses = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1';

  return (
    <div className="flex flex-wrap gap-1">
      {displayTags.map((tag) => (
        <span
          key={tag.id}
          className={`inline-flex items-center rounded-full ${sizeClasses}`}
          style={{
            backgroundColor: tag.color ? `${tag.color}20` : '#f3f4f6',
            color: tag.color || '#4b5563',
          }}
          title={tag.full_path || tag.name}
        >
          {tag.color && (
            <span
              className="w-2 h-2 rounded-full mr-1"
              style={{ backgroundColor: tag.color }}
            />
          )}
          {tag.name}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className={`inline-flex items-center rounded-full bg-gray-100 text-gray-600 ${sizeClasses}`}>
          +{remainingCount}
        </span>
      )}
    </div>
  );
}
